import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from './primitives.tsx';

/* ── Tabs ──────────────────────────────────────────────────────────────────
   Used by the player profile, player funds, points mall and support. Tab state
   belongs in the URL — "tab state lives in the URL query" — so this component
   is controlled and the caller owns the routing. */

export interface TabsProps<T extends string> {
  tabs: readonly { key: T; label: string; count?: number }[];
  active: T;
  onChange: (key: T) => void;
  /** Announced to assistive tech; the visible heading usually says it already. */
  label: string;
}

export function Tabs<T extends string>({ tabs, active, onChange, label }: TabsProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  // Arrow-key navigation is what makes a tablist a tablist rather than a row of
  // buttons; without it the ARIA role promises behaviour that is not there.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = tabs.findIndex((t) => t.key === active);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = e.key === 'ArrowRight' ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
      onChange(tabs[next]!.key);
      ref.current?.querySelectorAll<HTMLButtonElement>('.tab')[next]?.focus();
    }
  };

  return (
    <div className="tabs" role="tablist" aria-label={label} ref={ref} onKeyDown={onKeyDown}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          className="tab"
          aria-selected={t.key === active}
          tabIndex={t.key === active ? 0 : -1}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.count !== undefined && <span className="nav-badge">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ── Filter chips ──────────────────────────────────────────────────────────
   "Status chips are mutually exclusive; combine with More filters." So this is
   a radio group in behaviour, and says so to assistive tech. */

export interface ChipsProps<T extends string> {
  options: readonly { key: T; label: string; count?: number }[];
  active: T;
  onChange: (key: T) => void;
  label: string;
}

export function Chips<T extends string>({ options, active, onChange, label }: ChipsProps<T>) {
  return (
    <div className="row" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={o.key === active}
          aria-pressed={o.key === active}
          className="chip"
          onClick={() => onChange(o.key)}
        >
          {o.label}
          {o.count !== undefined && <span className="text-subtle">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ── Drawer ────────────────────────────────────────────────────────────────
   The quick-look. "Row click opens the drawer. Full profile is a route" — so a
   drawer never replaces a route, it previews one. */

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

export function Drawer({ open, onClose, title, subtitle, actions, children }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  // See Dialog.tsx: onClose must stay out of the dependency array below.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    panel?.focus();

    const focusable = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    const onKey = (e: KeyboardEvent) => {
      /* INERT, IT TAKES NO KEYS. Behind the console's open menu the page is
         inert, and this drawer with it: no focus, no taps. But this listener is
         on the document, where inert does not reach — and it stops Escape — so
         with a drawer under the open menu, Escape closed the drawer and left
         the menu up. It asks instead. */
      if (panel?.closest('[inert]')) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      // aria-modal="true" tells assistive tech to ignore everything outside this
      // panel, so focus must not be able to leave it either — otherwise Tab lands
      // on a table row behind the scrim that the screen reader reports as nothing.
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = previous;
      // Focus goes back where it came from, or the row the user clicked is lost.
      restoreTo.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="drawer-backdrop" onMouseDown={onClose} />
      <aside
        ref={panelRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className="drawer-head">
          <div>
            <h3 className="drawer-title">{title}</h3>
            {subtitle && <div className="text-subtle drawer-subtitle">{subtitle}</div>}
          </div>
          <button
            type="button"
            className="btn btn-icon btn-secondary header-spacer"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="x" />
          </button>
        </header>
        <div className="drawer-body">{children}</div>
        {actions && <footer className="drawer-actions">{actions}</footer>}
      </aside>
    </>
  );
}

/* ── Pagination ────────────────────────────────────────────────────────── */

/**
 * The seven page numbers to offer around the current one.
 *
 * Exported because it is arithmetic worth testing on its own, and because the
 * window is the only interesting decision in the numbered mode: under eight
 * pages it shows them all, and above that it slides a seven-wide window that
 * stops at both ends rather than running past them.
 */
export function pageWindow(page: number, pages: number): number[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(page - 3, pages - 6));
  return Array.from({ length: 7 }, (_, i) => start + i);
}

/**
 * ONE PAGINATOR, IN TWO MODES.
 *
 * There were two. This one did Previous/Next, and `features/players/Pager.tsx`
 * did numbered pages — its own header said it existed only because this
 * component lacked them, which is a reason to extend this one rather than keep
 * a second. Two paginators with two looks on one console is itself a guidance
 * defect: an operator who learns the numbered one on Players meets a different
 * control on Transactions and has to work out whether it behaves the same.
 *
 * ── WHAT THE NUMBERED MODE BRINGS WITH IT ─────────────────────────────────
 *
 * Two behaviours that were only in the bespoke one, and both are kept because
 * both are right:
 *
 *   · IT STILL RENDERS ON A SINGLE PAGE. The Previous/Next mode hides itself
 *     when everything fits, which is correct for a control that would do
 *     nothing. The numbered mode keeps the COUNT — "Showing 1–8 of 8" is a fact
 *     an operator reads even when there is nowhere to page to, and on a filtered
 *     list it is the answer to "how many matched".
 *
 *   · IT REFUSES TO INVENT A RANGE PAST THE END. A page beyond the last returns
 *     no rows, so there is no window to describe; "Showing 785–792 of 214" is
 *     arithmetic, not a fact about the response. It says so instead.
 */
export function Pagination({
  page,
  perPage,
  total,
  onPage,
  numbered = false,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (page: number) => void;
  /** Numbered page buttons and a persistent count, rather than Previous/Next. */
  numbered?: boolean;
}) {
  const pages = Math.max(1, Math.ceil(total / perPage));

  if (numbered) {
    /* A page past the end returns no rows, so there is no window to describe. */
    const beyond = page > pages;
    const from = total === 0 || beyond ? 0 : (page - 1) * perPage + 1;
    const to = beyond ? 0 : Math.min(page * perPage, total);

    return (
      <nav className="pagination" aria-label="Pagination">
        <span>
          {beyond ? (
            <>
              Page {page.toLocaleString('en-PH')} is past the last page (
              {pages.toLocaleString('en-PH')}) · {total.toLocaleString('en-PH')} in this view
            </>
          ) : (
            <>
              Showing {from.toLocaleString('en-PH')}–{to.toLocaleString('en-PH')} of{' '}
              {total.toLocaleString('en-PH')}
            </>
          )}
        </span>
        {pages > 1 &&
          pageWindow(page, pages).map((n) => (
            <button
              key={n}
              type="button"
              className={
                n === page ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'
              }
              aria-label={`Page ${n}`}
              aria-current={n === page ? 'page' : undefined}
              onClick={() => onPage(n)}
            >
              {n}
            </button>
          ))}
        {pages > 1 && (
          <button
            type="button"
            className="btn btn-icon btn-secondary"
            aria-label="Next page"
            disabled={page >= pages}
            onClick={() => onPage(page + 1)}
          >
            <Icon name="caret-right" />
          </button>
        )}
      </nav>
    );
  }

  if (total <= perPage) return null;
  const first = (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);

  return (
    <nav className="pagination" aria-label="Pagination">
      <span>
        {first.toLocaleString('en-PH')}–{last.toLocaleString('en-PH')} of{' '}
        {total.toLocaleString('en-PH')}
      </span>
      <button
        type="button"
        className="btn btn-secondary btn-sm header-spacer"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        <Icon name="caret-left" />
        Previous
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      >
        Next
        <Icon name="caret-right" />
      </button>
    </nav>
  );
}
